import {PropertyValues} from "lit";
import {property} from "lit/decorators/property.js";
import {state} from "lit/decorators/state.js";
import {merge, ReplaySubject, takeUntil} from "rxjs";
import {container} from "tsyringe";
import {FormProxy} from "../../advanced-form/form.proxy";
import {ProxyService} from "../../advanced-form/proxy.service";
import {Entity} from "../../domain/entities/entity";
import {FormHelper} from "../../helpers/form.helper";
import {EntityState} from "../../persistence/entity-state.enum";
import {EntityManager} from "../../persistence/entity.manager";
import {ClassOf} from "../../types/class-of.type";
import {AbstractDialogComponent} from "./abstract-dialog.component";
import {FormConfiguration} from "../../advanced-form/form-configuration.type";
import {renderTextColorField} from "../_inputs/ha-input-color/text-color-field.template";

export abstract class AbstractEntityDialogComponent<TValue extends Entity<TValue>> extends AbstractDialogComponent {

    @property({type: Boolean}) public creationMode: ClassOf<TValue> | null = null;

    protected abstract _formSettings: Array<FormConfiguration<TValue>>;

    @state() protected _isSavable = false;

    protected _lastEntityChangeTick = -1;

    protected changed$ = new ReplaySubject<any>();

    @state()
    protected form: FormProxy<TValue> | null = null;

    protected _entity: TValue | null = null;

    @property({type: Object})
    public get entity(): TValue | null {
        return this._entity;
    }

    public set entity(entity: TValue | null) {
        if (!entity || this._lastEntityChangeTick === entity.lastChangeTick) {
            return;
        }

        if (this.entity === entity) {
            return;
        }

        this._entity = entity;
        this._lastEntityChangeTick = entity.lastChangeTick;

        entity.persisted$.pipe(
            takeUntil(this._disconnected$)
        ).subscribe(() => this.form?.commit());

        this._initForm(entity);

        this.requestUpdate();
    }

    public async createNewEntity(): Promise<void> {
        if (!this.creationMode) {
            return;
        }

        const entityManager = container.resolve(EntityManager);
        const newEntity = await entityManager.getRepositoryForEntity<TValue>(this.creationMode)!.create();
        if (!newEntity) {
            console.warn(`Konnte keine neue Ressource vom Typ ${this.creationMode.name} anlegen`)
            return;
        }

        entityManager.setLoadedEntity(newEntity, EntityState.NEW);
        this.entity = newEntity;
    }

    public disconnectedCallback() {
        this.changed$.complete();
        this.changed$.unsubscribe();
        super.disconnectedCallback();
    }

    protected shouldUpdate(_changedProperties: PropertyValues): boolean {
        return !!this.form && super.shouldUpdate(_changedProperties);
    }

    protected _initForm(entity: TValue | null): void {
        const proxyService = container.resolve<ProxyService<TValue>>(ProxyService);

        if (!entity) {
            return;
        }

        if (!proxyService.has(entity.uid)) {
            const promisedForm = FormHelper.build(entity.uid, entity, this._formSettings)
            proxyService.register(entity.uid, promisedForm);
        }

        proxyService.get(entity.uid)!.then(form => {
                if (!form) {
                    throw new Error(`Kein Formular für die Entität mit der UUID ${entity.uid}`);
                }

                this.form = form;

                merge(
                    form.changedValue$,
                    form.reset$,
                    form.commit$
                ).pipe(
                    takeUntil(this._disconnected$)
                ).subscribe(data => {
                    if (!!data) {
                        this.changed$.next(data);
                        this.requestUpdate();
                    }

                    this._checkIsSavable();
                    this._isDirty = this.form?.dirty ?? false;
                });
            }
        );
    }

    /** The text colour field and the sample (feature plan 0074), from the current form values; needs a
     *  `textColor` entry in `_formSettings`. */
    protected _renderTextColorField(sampleText: string): unknown {
        const form = this.form!;
        const {color, textColor} = form.value;
        return renderTextColorField({
            form,
            binding: form.bindToFn(`textColor`),
            color: color ?? null,
            textColor: textColor ?? null,
            sampleText,
            onChange: this._requestRender,
        });
    }

    private _checkIsSavable(): void {
        this._isSavable = !!this.form?.dirty && !!this.form?.valid;
    }
}